import React, { useEffect, useMemo } from "react"
import { Box } from "@chakra-ui/react"
import EditorPane from "components/CanvasEditor/EditorPane"
import PropertyRow from "components/CanvasEditor/Inspector/PropertyRow"
import Select from "components/ReactHookForm/Select"
import {
  BADGE_HORIZONTAL_ALIGNMENT_OPTIONS,
  BADGE_VERTICAL_ALIGNMENT_OPTIONS,
  BADGE_TEXT_DECORATION_OPTIONS,
  BADGE_TEXT_TRANSFORM_OPTIONS,
} from "components/BadgeBuilder/utils/constants"
import {
  BADGE_FONT_VALUES, BADGE_FONT_WEIGHT_OPTIONS,
  BADGE_FONT_SUPPORTED_WEIGHTS, BADGE_FONT_WEIGHTS, BadgeFontType,
} from "components/BadgeBuilder/utils/badgeFonts"
import NumberProperty from "components/CanvasEditor/Inspector/NumberProperty"
import { MIXED, useIsMixed } from "components/CanvasEditor/Inspector/MixedValues"
import ToggleButtonGroup, { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"
import { Control, useController, useWatch } from "react-hook-form"
import {
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignJustifyIcon,
  ArrowUpToLineIcon,
  FoldVerticalIcon,
  ArrowDownToLineIcon,
  TypeIcon,
  ItalicIcon,
  CaseSensitiveIcon,
  UnderlineIcon,
  StrikethroughIcon,
  MinusIcon,
  CaseUpperIcon,
  CaseLowerIcon,
} from "components/BadgeBuilder/Icons"
import ConstrainedColorPicker from "components/BadgeBuilder/ReactHookForm/ConstrainedColorPicker"
import { BadgeElementFormValues } from "components/BadgeBuilder/Panels/EditorPanel/BadgeSidePanelForm"

type Props = {
  control: Control<BadgeElementFormValues>
}

const TextStyleFields = ({ control }: Props) => {
  const selectedFont = useWatch({
    control,
    name: "font",
  }) as BadgeFontType

  const { field: weightField } = useController({
    control,
    name: "weight",
  })

  const currentWeight = weightField.value
  const fontMixed = useIsMixed("font")
  const weightMixed = useIsMixed("weight")

  const weightOptions = useMemo(() => {
    if (!selectedFont) {
      return BADGE_FONT_WEIGHT_OPTIONS
    }

    const supportedWeights = BADGE_FONT_SUPPORTED_WEIGHTS[selectedFont]
      || [BADGE_FONT_WEIGHTS.Regular, BADGE_FONT_WEIGHTS.Bold]

    return BADGE_FONT_WEIGHT_OPTIONS.filter((option) => supportedWeights.includes(option.value))
  }, [selectedFont])

  useEffect(() => {
    if (!selectedFont || !currentWeight) return

    const supportedWeights = BADGE_FONT_SUPPORTED_WEIGHTS[selectedFont]

    // If current weight isn't supported, fall back to Regular or first available
    if (!supportedWeights.includes(currentWeight)) {
      const fallbackWeight = supportedWeights.includes(BADGE_FONT_WEIGHTS.Regular)
        ? BADGE_FONT_WEIGHTS.Regular
        : supportedWeights[0]
      weightField.onChange(fallbackWeight)
    }
  }, [selectedFont, currentWeight, weightField.onChange])

  const horizontalAlignmentOptions = BADGE_HORIZONTAL_ALIGNMENT_OPTIONS.map((option) => ({
    button: (selected: boolean, onClick: () => void) => (
      <ToggleButtonGroupItem
        selected={selected}
        onClick={onClick}
        title={`Align ${option.label}`}
        size="sm"
      >
        {option.value === "left" && <AlignLeftIcon size="sm" />}
        {option.value === "center" && <AlignCenterIcon size="sm" />}
        {option.value === "right" && <AlignRightIcon size="sm" />}
        {option.value === "justify" && <AlignJustifyIcon size="sm" />}
      </ToggleButtonGroupItem>
    ),
    value: option.value,
  }))

  const verticalAlignmentOptions = BADGE_VERTICAL_ALIGNMENT_OPTIONS.map((option) => ({
    button: (selected: boolean, onClick: () => void) => (
      <ToggleButtonGroupItem
        selected={selected}
        onClick={onClick}
        title={`Vertical Align ${option.label}`}
        size="sm"
      >
        {option.value === "top" && <ArrowUpToLineIcon size="sm" />}
        {option.value === "center" && <FoldVerticalIcon size="sm" />}
        {option.value === "bottom" && <ArrowDownToLineIcon size="sm" />}
      </ToggleButtonGroupItem>
    ),
    value: option.value,
  }))

  const decorationOptions = BADGE_TEXT_DECORATION_OPTIONS.map((option) => ({
    button: (selected: boolean, onClick: () => void) => (
      <ToggleButtonGroupItem
        selected={selected}
        onClick={onClick}
        title={option.label}
        size="sm"
      >
        {option.value === "none" && <CaseSensitiveIcon size="sm" />}
        {option.value === "underline" && <UnderlineIcon size="sm" />}
        {option.value === "strikethrough" && <StrikethroughIcon size="sm" />}
      </ToggleButtonGroupItem>
    ),
    value: option.value,
  }))

  const transformOptions = BADGE_TEXT_TRANSFORM_OPTIONS.map((option) => ({
    button: (selected: boolean, onClick: () => void) => (
      <ToggleButtonGroupItem
        selected={selected}
        onClick={onClick}
        title={option.label}
        size="sm"
      >
        {option.value === "none" && <MinusIcon size="sm" />}
        {option.value === "capitalize" && <CaseSensitiveIcon size="sm" />}
        {option.value === "uppercase" && <CaseUpperIcon size="sm" />}
        {option.value === "lowercase" && <CaseLowerIcon size="sm" />}
      </ToggleButtonGroupItem>
    ),
    value: option.value,
  }))

  return (
    <EditorPane title="Text Style">
      <PropertyRow label="Font">
        <Select
          name="font"
          control={control}
          options={BADGE_FONT_VALUES.map((font) => ({ label: font, value: font }))}
          placeholder={fontMixed ? MIXED : undefined}
          mb={0}
          vertical
          size="sm"
        />
      </PropertyRow>

      <PropertyRow label="Weight">
        <Select
          name="weight"
          control={control}
          options={weightOptions}
          placeholder={weightMixed ? MIXED : undefined}
          mb={0}
          size="sm"
        />
      </PropertyRow>

      <PropertyRow label="Size (pt)">
        <NumberProperty
          name="fontSize"
          control={control}
          min={1}
          max={72}
          step={1}
          precision={2}
          w="100%"
          size="sm"
        />
      </PropertyRow>

      <PropertyRow label="Color">
        <ConstrainedColorPicker name="color" control={control} showHelpText={false} size="sm" />
      </PropertyRow>

      <PropertyRow label="Vert. Align">
        <ToggleButtonGroup
          name="verticalAlign"
          control={control}
          columns={3}
          spacing={2}
        >
          {verticalAlignmentOptions}
        </ToggleButtonGroup>
      </PropertyRow>

      <PropertyRow label="Horiz. Align">
        <ToggleButtonGroup
          name="align"
          control={control}
          columns={4}
          spacing={2}
        >
          {horizontalAlignmentOptions}
        </ToggleButtonGroup>
      </PropertyRow>

      <ItalicToggle control={control} />

      <PropertyRow label="Decoration">
        <ToggleButtonGroup
          name="textDecoration"
          control={control}
          columns={3}
          spacing={2}
        >
          {decorationOptions}
        </ToggleButtonGroup>
      </PropertyRow>

      <PropertyRow label="Transform">
        <ToggleButtonGroup
          name="textTransform"
          control={control}
          columns={4}
          spacing={2}
        >
          {transformOptions}
        </ToggleButtonGroup>
      </PropertyRow>
    </EditorPane>
  )
}

const ItalicToggle = ({ control }: { control: Control<BadgeElementFormValues> }) => {
  const { field } = useController({
    control,
    name: "italic",
  })
  const mixed = useIsMixed("italic")

  return (
    <PropertyRow label="Style">
      <Box display="flex" gap={2}>
        <ToggleButtonGroupItem
          selected={!field.value && !mixed}
          onClick={() => field.onChange(false)}
          title="Normal"
          size="sm"
          w="auto"
          p={2}
        >
          <TypeIcon size="sm" />
        </ToggleButtonGroupItem>
        <ToggleButtonGroupItem
          selected={!!field.value && !mixed}
          onClick={() => field.onChange(true)}
          title="Italic"
          size="sm"
          w="auto"
          p={2}
        >
          <ItalicIcon size="sm" />
        </ToggleButtonGroupItem>
      </Box>
    </PropertyRow>
  )
}

export default TextStyleFields
